/**
 * theme.js — entry point del tema (caricato come <script type="module">).
 * GSAP, ScrollTrigger e Lenis sono già globali (script UMD in theme.liquid);
 * Three.js è risolto via import map. Qui avviamo i moduli condivisi.
 */
// .catch: un import a livello di modulo che fallisce (rete, import map assente)
// resterebbe una unhandled rejection a ogni caricamento di pagina.
const motionCore = import('@theme/motion-core').catch(() => null);
const cartDrawer = import('@theme/cart-drawer').catch(() => null);

/* ----- altezza dell'header sticky in --header-h -----
   Il primo valore lo scrive lo script inline di theme.liquid (prima del paint, così
   l'hero non si assesta). Qui si tiene aggiornato su resize e rotazione dello schermo.
   Il menu mobile aperto è position:absolute e non altera l'altezza: nessun caso a parte. */
function trackHeaderHeight() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const apply = () => {
    document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
  };
  apply();
  if (window.ResizeObserver) new ResizeObserver(apply).observe(header);
  else window.addEventListener('resize', apply);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trackHeaderHeight);
} else {
  trackHeaderHeight();
}

/* ----- indicatore "scorri" dell'hero: porta alla prima sezione che riempie il frame ----- */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-hero-scroll]') : null;
  if (!btn) return;

  // Sotto metà frame è una barra di servizio (vantaggi, annunci) o una sezione che non
  // rende nulla, non una destinazione: fermarsi lì lascerebbe in frame la striscia più
  // il taglio della sezione successiva. Rete di sicurezza: sulla home di oggi la sezione
  // dopo l'hero è già il video a schermo intero e questa regola non scatta.
  const MIN_FRAME_FILL = 0.5;

  const section = btn.closest('.shopify-section') || btn.closest('.hero');
  let next = section && section.nextElementSibling;
  while (next && next.offsetHeight < window.innerHeight * MIN_FRAME_FILL) {
    const after = next.nextElementSibling;
    if (!after) break; // era l'ultima: meglio atterrarci che non muoversi affatto
    next = after;
  }
  if (!next) return;

  // Una sezione che riempie il frame si allinea al viewport invece di finire sotto
  // l'header: l'header è traslucido ed è pensato per sovrapporsi, mentre spingerla giù
  // le taglierebbe il fondo — ed è lì che stanno le CTA delle sezioni a schermo intero.
  // 0.85 e non 0.9: al primo frame innerHeight è lo svh, ma se su mobile la barra URL
  // si è già ritratta diventa lvh e una sezione da 100svh scenderebbe sotto una soglia
  // più stretta. Sbagliare per difetto è innocuo: una sezione normale ha padding 6rem,
  // quindi il titolo parte comunque più in basso dell'header.
  const FULL_BLEED_FILL = 0.85;

  const header = document.querySelector('.site-header');
  const fullBleed = next.offsetHeight >= window.innerHeight * FULL_BLEED_FILL;
  const offset = fullBleed || !header ? 0 : header.offsetHeight;

  // con lo smooth scroll attivo bisogna passare da Lenis: window.scrollTo nativo
  // litigherebbe con il suo loop rAF
  const mod = await motionCore;
  const lenis = mod && typeof mod.getLenis === 'function' ? mod.getLenis() : null;
  if (lenis) {
    lenis.scrollTo(next, { offset: -offset });
    return;
  }

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: next.getBoundingClientRect().top + window.scrollY - offset,
    behavior: reduce ? 'auto' : 'smooth',
  });
});

/* ----- video responsive: sceglie la sorgente mobile/desktop mantenendo l'autoplay ----- */
function initResponsiveVideo() {
  const vids = document.querySelectorAll('video[data-src-desktop]');
  if (!vids.length) return;
  const mq = window.matchMedia('(max-width: 767px)');
  const apply = () => {
    vids.forEach((v) => {
      const next = mq.matches && v.dataset.srcMobile ? v.dataset.srcMobile : v.dataset.srcDesktop;
      if (!next || v.getAttribute('src') === next) return;
      v.setAttribute('src', next);
      v.load();
      const p = v.play();
      if (p) p.catch(() => {});
    });
  };
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply); // Safari datati
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initResponsiveVideo);
} else {
  initResponsiveVideo();
}

/* ----- disclosure dell'header (selettore lingua, ricerca): chiude al click fuori o con Esc -----
   I <details> restano funzionanti anche senza questo JS: qui si aggiunge solo la chiusura. */
function closeDisclosures(except) {
  document.querySelectorAll('[data-disclosure][open]').forEach((el) => {
    if (el !== except) el.open = false;
  });
}
document.addEventListener('click', (e) => {
  const inside = e.target instanceof Element ? e.target.closest('[data-disclosure]') : null;
  closeDisclosures(inside);
});
document.addEventListener('focusout', (e) => {
  const sw = e.target instanceof Element ? e.target.closest('[data-disclosure][open]') : null;
  if (!sw) return;
  // relatedTarget nullo = il focus non è passato a un altro elemento focusabile
  // (es. Safari che non mette a fuoco i button al click): lasciare aperto, altrimenti
  // la tendina si chiuderebbe prima che il click sul button faccia il submit.
  if (!(e.relatedTarget instanceof Element)) return;
  if (sw.contains(e.relatedTarget)) return;
  sw.open = false;
});
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = document.querySelector('[data-disclosure][open]');
  if (!open) return;
  open.open = false;
  const toggle = open.querySelector('summary');
  if (toggle) toggle.focus();
});

/* ----- risincronizzazione del carrello -----
   Il carrello e' del visitatore, ma la pagina che lo descrive e' una fotografia
   presa al caricamento e puo' invecchiare senza che succeda niente qui dentro:
   un'altra scheda dello stesso browser che aggiunge lo stesso gioiello, un
   ritorno «indietro» servito dalla bfcache con sopra i numeri di prima, una
   pagina rimasta aperta mezz'ora. In tutti questi casi il tetto «quanti ne puoi
   ancora aggiungere» sarebbe calcolato su un carrello che non esiste piu'.

   Si richiede /cart.js — poche centinaia di byte, la risposta e' per sessione —
   e lo si ridistribuisce come 'cart:updated': lo stesso evento che gia' arriva
   dopo un'aggiunta, quindi chi ascolta non deve sapere da dove viene.

   NON risolve la giacenza: quella e' condivisa con gli altri clienti e cambia
   quando comprano loro. Li' l'unica risposta vera e' quella di Shopify alla
   chiamata, ed e' gia' gestita (422 col motivo a video).

   L'intervallo minimo evita una raffica di richieste con l'alt-tab ripetuto. */
const RESYNC_EVERY = 10000;
let lastResync = 0;
let resyncToken = 0;

/* Quante volte un carrello e' gia' stato distribuito, da qualunque fonte. Il
   token qui sotto mette in fila le risincronizzazioni fra loro, ma non le
   difende da chi porta un carrello per altre strade: una mutazione dal drawer
   (+/−/rimuovi) risponde con il carrello aggiornato e lo distribuisce da se'.
   Se e' successo mentre la nostra richiesta era in volo, la nostra e' gia'
   vecchia e va lasciata cadere invece di rimettere in giro i numeri di prima. */
let cartEpoch = 0;
document.addEventListener('cart:updated', () => {
  cartEpoch += 1;
});

async function resyncCart(force) {
  const now = Date.now();
  /* Orologio spostato indietro (correzione di rete, fuso cambiato a mano): la
     differenza resta negativa e la finestra non scadrebbe piu' per tutto il
     salto, lasciando i numeri fermi anche per ore. Si riparte da adesso. */
  if (now < lastResync) lastResync = 0;
  if (!force && now - lastResync < RESYNC_EVERY) return;
  lastResync = now;

  /* Due risincronizzazioni possono accavallarsi — un'aggiunta appena riuscita e
     il rientro nella scheda — e le risposte possono tornare in ordine inverso:
     quella partita per prima arriverebbe per ultima e rimetterebbe in giro un
     carrello gia' superato. Comanda l'ultima partita; le altre si scartano
     invece di annullare la richiesta, perche' /cart.js e' minuscolo e la
     risposta potrebbe servire comunque a chi l'ha chiesta dopo. */
  const token = ++resyncToken;
  const epoch = cartEpoch;
  const superseded = () => token !== resyncToken || epoch !== cartEpoch;

  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok || superseded()) return;
    const cart = await res.json();
    if (superseded()) return;
    const mod = await cartDrawer;
    if (superseded()) return;
    if (mod) mod.updateCartCount(cart);
    else document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  } catch (err) {
    /* offline o richiesta annullata: si resta sui numeri di prima, e a decidere
       sara' comunque Shopify quando il cliente prova ad aggiungere */
  }
}

/* persisted = la pagina torna dalla bfcache, quindi con il DOM di prima e senza
   che nessuno script sia ripartito: e' il caso in cui i numeri sono piu' vecchi. */
window.addEventListener('pageshow', (e) => {
  if (e.persisted) resyncCart(true);
});
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) resyncCart(false);
});

/* ----- selettori di quantita' (scheda prodotto e carrello) -----
   Due mestieri in un posto solo, perche' i campi sono gli stessi.

   1. Verso /cart parte sempre un intero in cifre. I pulsanti che aggiornano
      hanno formnovalidate — la casella di consenso obbligatoria vive nello
      stesso form — quindi min e max non vengono fatti rispettare dal browser e
      passa qualunque cosa ci sia nel campo. Un valore che il carrello non sa
      leggere vale zero, cioe' una riga tolta senza che nessuno l'abbia chiesta.
      Ci finisce: "" (testo incollato in un input number), un negativo, un
      decimale, e "1e3" — notazione valida per un input number che come stringa
      varrebbe pero' 1. Quindi non basta scartare: anche i valori buoni si
      riscrivono col numero interpretato. isSafeInteger e non isInteger perche'
      da 1e21 in su String() torna alla notazione esponenziale, rimettendo in
      campo proprio la forma che stiamo togliendo.

   2. Non si ordina piu' di quanto ce n'e'. Il tetto lo scrive Liquid in max,
      e solo quando Shopify conosce la giacenza e nega gli ordini oltre il
      disponibile. Senza, Shopify tronca da solo e risponde 200: la pagina
      tornava con un numero diverso da quello scritto e nessuno spiegava perche'.

   Il ripiego e' l'attributo value scritto da Liquid: sul carrello e' la
   quantita' della riga ("lascia stare questa riga"), sulla scheda prodotto e' 1. */

/** Nota associata al campo via aria-describedby: la trova anche chi legge. */
function qtyNote(input) {
  const id = input.getAttribute('aria-describedby');
  return id ? document.getElementById(id) : null;
}

function showQtyNote(input, text) {
  const note = qtyNote(input);
  if (!note) return;
  note.textContent = text || '';
  note.hidden = !text;
}

/* Singolare o forma con il numero, come la riga «Disponibilita'» della scheda.
   Regex globale e non stringa: replace con una stringa sostituisce solo la prima
   occorrenza, e una traduzione puo' nominare il numero piu' di una volta. */
function qtyMaxMessage(input, max) {
  const raw = max === 1 ? input.dataset.maxOne : input.dataset.maxOther;
  return (raw || '').replace(/%count%/g, max);
}

/** Tetto della riga, o null quando Shopify non lo conosce (max assente). */
function qtyMax(input) {
  const max = Number(input.max);
  return input.max !== '' && Number.isSafeInteger(max) && max >= 0 ? max : null;
}

/** Riporta il campo a un intero leggibile e non oltre il tetto.
    true se ha dovuto tagliare sul massimo (e quindi c'e' da spiegarlo). */
function normaliseQty(input) {
  const n = Number(input.value);
  const usable = input.value.trim() !== '' && Number.isSafeInteger(n) && n >= 0;
  if (!usable) {
    input.value = input.defaultValue;
    return false;
  }
  const max = qtyMax(input);
  if (max !== null && n > max) {
    input.value = String(max);
    return true;
  }
  input.value = String(n);
  return false;
}

const qtyInput = (e) =>
  e.target instanceof Element ? e.target.closest('input[data-qty-input]') : null;

/* Mentre si scrive si avvisa e basta: tagliare a ogni tasto vorrebbe dire che
   con tetto 3, digitando "12", il campo diventa 3 a meta' battuta. */
document.addEventListener('input', (e) => {
  const input = qtyInput(e);
  if (!input) return;
  const max = qtyMax(input);
  const n = Number(input.value);
  const over = max !== null && Number.isFinite(n) && n > max;
  showQtyNote(input, over ? qtyMaxMessage(input, max) : '');
});

/* Alla conferma — uscita dal campo, Invio, frecce del selettore — si taglia. */
document.addEventListener('change', (e) => {
  const input = qtyInput(e);
  if (!input) return;
  const max = qtyMax(input);
  showQtyNote(input, normaliseQty(input) ? qtyMaxMessage(input, max) : '');
});

/* Rete di sicurezza sul submit. form.elements e non querySelectorAll: sulla
   scheda prodotto il campo quantita' sta FUORI dal form e ci si aggancia con
   l'attributo form="…", quindi fra i discendenti non c'e'. La lista dei valori
   si costruisce dopo i listener di submit: qui si fa ancora in tempo. */
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof Element) || !form.elements) return;
  Array.from(form.elements).forEach((input) => {
    if (!input.matches || !input.matches('input[data-qty-input]')) return;
    const max = qtyMax(input);
    if (normaliseQty(input)) showQtyNote(input, qtyMaxMessage(input, max));
  });
});

/* ----- carrello AJAX (usa solo le route native Shopify /cart/*.js) ----- */

/* Mostra nel form il motivo dato da Shopify. Restituisce false se il form non ha
   un posto dove scriverlo, cosi' il chiamante ripiega sul messaggio generico. */
function showCartError(form, message) {
  const box = form.querySelector('[data-cart-error]');
  if (!box) return false;
  box.textContent = message;
  box.hidden = !message;
  return true;
}

/* Il corpo di un 422 di /cart/add.js contiene la spiegazione gia' tradotta da
   Shopify, es. "All 1 Anello Fenice are in your cart." */
async function addErrorMessage(res) {
  try {
    const data = await res.json();
    return data.description || data.message || '';
  } catch (parseErr) {
    return ''; // corpo non JSON: nessun motivo da mostrare
  }
}

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[action$="/cart/add"][data-ajax-cart]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('[type="submit"]');
  const original = btn && btn.textContent;
  showCartError(form, ''); // pulisce l'esito del tentativo precedente
  if (btn) {
    btn.disabled = true;
    /* Segnala a product-form.js che il pulsante e' occupato: un cambio variante
       durante la richiesta lo riaprirebbe, e un secondo click aggiungerebbe una
       seconda riga con l'altra variante. Lo stato giusto lo rilegge il finally
       da data-state, che product-form.js continua ad aggiornare. */
    btn.dataset.busy = '1';
    btn.textContent = form.dataset.adding || 'Aggiungo…';
  }
  try {
    const body = new FormData(form);
    // chiede a Shopify anche l'HTML aggiornato del drawer (Section Rendering API)
    body.append('sections', 'cart-drawer');

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });

    if (!res.ok) {
      // 422: articolo non aggiungibile — scorta esaurita, o gia' tutta nel carrello.
      // Succede sempre sui pezzi unici: "Riprova" non spiegherebbe nulla e il
      // cliente continuerebbe a cliccare a vuoto.
      const reason = await addErrorMessage(res);
      if (reason && showCartError(form, reason)) {
        /* Il motivo e' gia' a video: lasciare "Aggiungo…" per 1,4s lo
           contraddirebbe. Da qui in poi il pulsante e' marcato busy, quindi
           nemmeno un cambio variante lo rimetterebbe a posto: va scritto ora.
           Il finally fa comunque il ripristino definitivo da data-state. */
        if (btn) btn.textContent = original;
        /* Un rifiuto vuol dire quasi sempre che i nostri conti erano vecchi —
           tipicamente il pezzo e' gia' tutto nel carrello, magari messo da
           un'altra scheda. Si ripartono dai numeri veri, cosi' il pulsante si
           assesta sullo stato giusto invece di riproporre un tentativo che
           fallirebbe di nuovo. Senza await: l'errore deve comparire subito. */
        resyncCart(true);
        return;
      }
      throw new Error('add failed');
    }
    const added = await res.json();
    // l'articolo è nel carrello: segna subito l'esito positivo
    if (btn) btn.textContent = form.dataset.added || 'Aggiunto ✓';

    // drawer e contatore sono rifiniture: un loro errore non deve annullare l'aggiunta riuscita
    try {
      const mod = await cartDrawer;
      if (mod) {
        mod.renderCartDrawer(added.sections && added.sections[mod.SECTION_ID]);
        mod.openCartDrawer();
      }
      /* Fuori dal ramo del drawer: e' questa risincronizzazione a ricalcolare
         quanti pezzi si possono ANCORA aggiungere dopo l'aggiunta appena
         riuscita, e serve anche se il pannello laterale non ha caricato. */
      await resyncCart(true);
    } catch (refreshErr) {
      /* drawer non aggiornato: l'articolo è comunque nel carrello */
    }
  } catch (err) {
    if (btn) btn.textContent = 'Riprova';
  } finally {
    setTimeout(() => {
      if (!btn) return;
      /* Prima di tutto: il pulsante torna a product-form.js, che da qui in poi
         puo' di nuovo scriverci sopra a ogni cambio variante. */
      delete btn.dataset.busy;
      /* Sulla scheda prodotto lo stato del pulsante puo' essere cambiato mentre
         l'esito era a video: per un cambio variante, o perche' l'aggiunta appena
         riuscita ha esaurito quello che si poteva ancora aggiungere. Stato e
         testo li tiene aggiornati product-form.js in data-state/data-state-label
         (il primo valore lo scrive Liquid), quindi qui si rilegge invece di
         ricostruire: la corrispondenza stato→etichetta resta in un posto solo.
         Gli altri form non hanno l'attributo e ricadono sul testo di partenza. */
      const state = btn.dataset.state;
      if (!state) {
        btn.disabled = false;
        btn.textContent = original;
        return;
      }
      btn.disabled = state !== 'ok';
      btn.textContent = btn.dataset.stateLabel || original;
    }, 1400);
  }
});
